import { useCallback } from "react";

import { type FormField, type FormValues, prepareFormValues, useDynamicForm } from "@prequel/import-react";

import { type DatalakeOptions, type DatalakeUpdateOptions } from "@/api/import/datalakes";
import { fetchDatalakeForm, type FormMode } from "@/api/import/forms";

export const DATALAKE_ROOT_KEY = "datalake";

export type UseDatalakeFormResult = {
  draft: FormValues;
  fields: FormField[];
  setFieldValue: (field: FormField, value: unknown) => void;
  getDatalake: () => DatalakeOptions;
  getDatalakeUpdate: () => DatalakeUpdateOptions;
  isLoading: boolean;
  isRefetching: boolean;
  error: unknown;
};

type UseDatalakeFormOptions = {
  initialValues?: FormValues;
  mode?: FormMode;
};

/**
 * Drives the datalake form from the fields the API generates for the current draft.
 * Changing a field marked `refetch_on_change` (storage, auth method) re-requests it.
 *
 * In edit mode the API marks the fields a PATCH cannot change as read-only, so they
 * render as values rather than inputs and stay out of the payload.
 */
export function useDatalakeForm({
  initialValues,
  mode = "create",
}: UseDatalakeFormOptions = {}): UseDatalakeFormResult {
  const fetchForm = useCallback((values: FormValues) => fetchDatalakeForm(values, mode), [mode]);
  const { values, fields, setFieldValue, isLoading, isRefetching, error } = useDynamicForm({
    fetchForm,
    rootKey: DATALAKE_ROOT_KEY,
    initialValues,
  });
  const getDatalake = useCallback(
    () => prepareFormValues(values, fields, DATALAKE_ROOT_KEY) as DatalakeOptions,
    [fields, values],
  );
  const getDatalakeUpdate = useCallback(
    () => prepareFormValues(values, fields, DATALAKE_ROOT_KEY) as DatalakeUpdateOptions,
    [fields, values],
  );

  return { draft: values, fields, setFieldValue, getDatalake, getDatalakeUpdate, isLoading, isRefetching, error };
}
